import {action, computed, flow, makeObservable, observable} from "mobx";
import * as _ from "lodash";

import {EAIClient} from "Services/EAIClient";
import {appStore} from "../";
import {dashboardOrder, orderCategories} from "Components/Dashboard/list";
import {Utils} from "../../common/utils";


interface DefaultResult {
    totalCount: number
}

export interface Dashboard {
    id: number
    workbook_id: string
    url: string
    name: string
    description: string
    thumbnail_url_300: string
    thumbnail_url_600: string
    short_name: string
    datasource?: { updated: string, time_span: string }
    published_date?: string
    updated?: string
    status?: string
    project?: string
    workbook_name?: string
    category?: string
    faculty_can_view?: boolean
    mentors_can_view?: boolean
    public_can_view?: boolean
    admins_can_view?: boolean
    datasource_name?: string
    custom_order?: number
    dashboard_type: string
    byo_dashboard_type?: string
    use_url?: boolean
    embedded_code?: string
    is_responsive?: boolean
}

export interface DashboardExport {
    "ruuid": string,
    "workbook_name": string,
    "tab": string,
    "sheet": string,
    "enflux_created": Date,
    "enflux_updated": Date
}

export interface DashboardCategoryInterface {
    [idx: string]: Dashboard[]
}

export interface SchoolResourceByCategory {
    data: Record<string, Dashboard[]>
}

export interface SchoolResourceDataQuery {
    data: Dashboard[],
    result: DefaultResult
}

export class DashboardStore {

    @observable dashboardsInCatgories: { [idx: string]: Dashboard[] };
    @observable dashboardTemplates: Dashboard[];
    @observable dashboardExports: DashboardExport[] = [];
    @observable dashboard?: Dashboard;
    @observable isDashboardProcessing: boolean;
    @observable dashboardsFavorites: Dashboard[];
    @observable resources: Dashboard[] = [];
    @observable totalResourcesCount = 0;

    constructor() {
        makeObservable(this);
    }

    @computed get dashboards(){
        return _.flatMap(this.dashboardsInCatgories, (dashArr: Dashboard[]) => dashArr.slice());
    }

    client = new EAIClient();

    fetchResources = flow(function *(this: DashboardStore, grouped = false, getAllStatus = true) {
        try {
            const resp = yield this.client.getDashboards(getAllStatus, false, grouped, "enflux_dashboard");
            this.totalResourcesCount = resp.data.result.total_count;
            this.resources = resp.data.data;
        } catch (e) {
            appStore.addError(e);
        }
    });

    addDashboard = flow(function *(this: DashboardStore, data: Partial<Dashboard>) {
        try {
            yield this.client.addBYOCDashboard(data);
        } catch (e) {
            appStore.addError(e);
        }
    });

    decodeDashboardInCategories = (newDashboards: { [p: string]: Dashboard[] }) => {
        for (const key in newDashboards) {
            // decode embedded code for BYODashboard and use_url false
            newDashboards[key] = newDashboards[key].map(dashboard => {
                if (dashboard.use_url === false && dashboard.embedded_code && dashboard.dashboard_type === "BYODashboard") {
                    dashboard.embedded_code = Utils.decodeBase64(dashboard.embedded_code);
                }
                return dashboard;
            });
        }
        return newDashboards;
    };

    @flow
    * getDashboardsInCategories(
        getAllDashboards = false,
        excludePPC = false,
        grouped = true,
        excluded_type: string | null = null,
        orderBy: orderCategories = "group",
        reloadCache = false){
            this.isDashboardProcessing = true;
            const desc = orderBy === "name desc" ? true : false;
            const order = orderBy.includes("name") ? "name" : orderBy;
        try {
            const resp = yield this.client.getDashboards(getAllDashboards, excludePPC, grouped, excluded_type, order, desc, reloadCache);
            const newDashboards = resp.data.data as { [p: string]: Dashboard[] };
            this.dashboardsInCatgories = this.decodeDashboardInCategories(newDashboards);
            if (this.dashboardsInCatgories["FAVORITE DASHBOARDS"]){
                this.dashboardsFavorites = this.dashboardsInCatgories["FAVORITE DASHBOARDS"];
            }
            this.isDashboardProcessing = false;
        } catch(e) {
            this.dashboardsInCatgories = {};
            appStore.addError(e);
            this.isDashboardProcessing = false;
        }
    }

    @flow
    * getDashboardTemplates(){
        try {
            const resp = yield this.client.getDashboardTemplates();
            this.dashboardTemplates = resp.data.data;
        } catch(e) {
            this.dashboardTemplates = [];
            appStore.addError(e);
        }
    }

    getDashboardById = flow(function *(this: DashboardStore, id: number | string, override?: string, callbackOnError?: (err: string) => void) {
        try {
            const resp = yield this.client.getDashboards();
            const newDashboards = resp.data.data as { [p: string]: Dashboard[] };
            this.dashboardsInCatgories = this.decodeDashboardInCategories(newDashboards);
            let dashboard = this._dashboardByFriendlyUrlName(id.toString());
            if(!dashboard){
                dashboard = this._dashboardById(parseInt(id.toString(), 10));
            }

            if(!dashboard){
                const errorMessage = "Could not access a dashboard with that ID. If you feel you have received this message in error, please contact your administrator.";
                if (callbackOnError) {
                    callbackOnError(errorMessage);
                } else {
                    appStore.addError(new Error(), errorMessage);
                }
            } else {
                if (dashboard.url) {
                    const urlParts = dashboard.url.split("/");

                    if(override){
                        urlParts[urlParts.length - 1] = override;
                        dashboard.url = urlParts.join("/");
                    }
                }
                if(!_.isEqual(this.dashboard, dashboard)){
                    // only update if different to prevent reloading of component
                    this.dashboard = dashboard;
                }
            }
        } catch(e) {
            this.dashboardsInCatgories = {};
            appStore.addError(e);
        }
    });

    deleteDashboard = flow(function *(this: DashboardStore, dashboardId: number) {
        try {
            yield this.client.deleteDashboard(dashboardId);
        } catch (e) {
            appStore.addError(e);
        }
    });

    deleteBYOCDashboard = flow(function *(this: DashboardStore, dashboardId: number) {
        try {
            yield this.client.deleteBYOCDashboard(dashboardId);
        } catch (e) {
            appStore.addError(e);
        }
    });


    updateThumbnail = flow(function *(this: DashboardStore, workbook: string, formData: FormData) {
        try {
            yield this.client.updateDashboardThumbnail(workbook, formData);
        } catch (e) {
            appStore.addError(e);
        }
    });

    @flow
    * registerDashboard(dashboard: Dashboard): any{
        this.isDashboardProcessing = true;
        try {
            yield this.client.registerDashboard(dashboard);
            this.isDashboardProcessing = false;
        } catch(e) {
            this.isDashboardProcessing = false;
            appStore.addError(e);
            throw e;
        }
    }

    @flow
    * updateDashboard(dashboard: Partial<Dashboard>): any{
        this.isDashboardProcessing = true;
        try {
            yield this.client.updateBYOCDashboard(dashboard);
            this.isDashboardProcessing = false;
        } catch(e) {
            this.isDashboardProcessing = false;
            appStore.addError(e);
            throw e;
        }
    }

    @flow
    * unregisterDashboard(dashboardId: number): any{
        this.isDashboardProcessing = true;
        try {
            yield this.client.unregisterDashboard(dashboardId);
            this.isDashboardProcessing = false;
        } catch(e) {
            this.isDashboardProcessing = false;
            appStore.addError(e);
            throw e;
        }
    }

    @flow
    * getDashboardExports(){
        if (!this.dashboard) {
            this.dashboardExports = [];
            return;
        }

        try {
            const resp = yield this.client.getDashboardExports(this.dashboard);
            this.dashboardExports = resp.data.data;
        } catch(e) {
            this.dashboardExports = [];
            appStore.addError(e);
        }
    }

    @flow
    * updateDashboardsOrder(custom_order: dashboardOrder[]): any{
        this.isDashboardProcessing = true;
        try {
            yield this.client.updateDashboardOrder(custom_order);
            this.isDashboardProcessing = false;
        } catch(e) {
            this.isDashboardProcessing = false;
            appStore.addError(e);
            throw e;
        }
    }

    @flow
    * addDashboardFavorites(dashboard_id: number){
    this.isDashboardProcessing = true;

        try {
            yield this.client.addFavoriteDashboard(dashboard_id);
            this.isDashboardProcessing = false;

        } catch (e) {
            appStore.addError(e);
            this.isDashboardProcessing = false;
            throw e;

        }
    }

    @flow
    * deleteDashboardFavorite(dasboard_id: number){
    this.isDashboardProcessing = true;

        try {
            yield this.client.deleteFavoriteDashBoard(dasboard_id);
            this.isDashboardProcessing = false;

        } catch (e) {
            appStore.addError(e);
            this.isDashboardProcessing = false;

            throw e;
        }
    }

    @action
    resetDashboard(){
        this.dashboard = undefined;
    }

    private _dashboardById(id: number){
        return _.find(this.dashboards, {id: id});
    }

    private _dashboardByFriendlyUrlName(name: string){
        return this.dashboards.find(dashboard => Utils.getUrlFriendlyString(dashboard.name) === name);
    }

    @flow
    * exportActionPlan(){
        if (!this.dashboard) {
            this.dashboardExports = [];
            return;
        }
        try {
            return this.client.exportActionPlans(this.dashboard);
        } catch(e) {
            appStore.addError(e);
        }
    }

}
